import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { listUsers, createUser, updateUser, deleteUser } from '../../../redux/actions/user';
import UserModal from '../UserModal';
import ConfirmationModal from '../../ui/ConfirmationModal';
import Portal from '../../ui/Portal';
import { MdDelete, MdEdit, MdPersonAdd } from 'react-icons/md';
import styles from './UserManagement.module.css';
import list from '../../../styles/shared/ManagementList.module.css';
import actions from '../../../styles/shared/RequestActions.module.css';

// Iniciales para el círculo de cada usuario: primera letra de las dos primeras palabras del nombre
// (o del correo si no tiene nombre).
const getInitials = ({ nombre, email }) => {
  const words = (nombre || '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return (email || '?').charAt(0).toUpperCase();
  return words.slice(0, 2).map((word) => word.charAt(0)).join('').toUpperCase();
};

// Validación del formulario antes de enviar al backend.
const validateUserForm = ({ nombre, email, password }, { isEdit = false } = {}) => {
  if (!nombre || !nombre.trim()) {
    return 'El nombre es obligatorio.';
  }
  if (!email || !email.trim()) {
    return 'El correo es obligatorio.';
  }
  if (!/@(profesor\.)?duoc\.cl$/i.test(email.trim())) {
    return 'El correo debe terminar en @duoc.cl o @profesor.duoc.cl.';
  }
  if (!isEdit && !password) {
    return 'La contraseña es obligatoria.';
  }
  return '';
};

const UserManagement = () => {
  const dispatch = useDispatch();
  const { users, loading, error } = useSelector(state => state.User);

  const [formValues, setFormValues] = useState({
    id: '',
    nombre: '',
    email: '',
    password: '',
    isStaff: false,
    estado: true,
  });
  const [formError, setFormError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  // Usuario a eliminar mientras se muestra la confirmación (null = cerrada)
  const [userToDelete, setUserToDelete] = useState(null);

  useEffect(() => {
    dispatch(listUsers());
  }, [dispatch]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormValues(prevValues => ({
      ...prevValues,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (formError) setFormError('');
  };

  const handleCreateUser = (e) => {
    e.preventDefault();
    const { nombre, email, password, isStaff, estado } = formValues;

    const validationError = validateUserForm({ nombre, email, password });
    if (validationError) {
      setFormError(validationError);
      return;
    }

    dispatch(createUser(nombre, email, password, isStaff, estado)).then(({ success, message }) => {
      if (success) {
        dispatch(listUsers()); // Reload the user list
        closeModal();
      } else {
        // Mantener el modal abierto y mostrar el error que trae el backend.
        setFormError(message);
      }
    });
  };
  const handleUpdateUser = (e) => {
    e.preventDefault();
    const { id, nombre, email, isStaff, estado, password } = formValues;

    const validationError = validateUserForm({ nombre, email, password }, { isEdit: true });
    if (validationError) {
      setFormError(validationError);
      return;
    }

    dispatch(updateUser(id, nombre, email, isStaff, estado, password)).then(({ success, message }) => {
      if (success) {
        dispatch(listUsers()); // Reload the user list
        closeModal();
      } else {
        setFormError(message);
      }
    });
  };

  const openCreateModal = () => {
    setIsEditMode(false);
    setFormError('');
    setFormValues({
      id: '',
      nombre: '',
      email: '',
      password: '',
      isStaff: false,
      estado: true,
    });
    setIsModalOpen(true);
  };

  const openEditModal = (user) => {
    setIsEditMode(true);
    setFormError('');
    setFormValues({
      id: user.id,
      nombre: user.nombre,
      email: user.email,
      isStaff: user.is_staff,
      estado: user.estado,
      password: '', // We don't set the password for editing
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setFormError('');
    setFormValues({
      id: '',
      nombre: '',
      email: '',
      password: '',
      isStaff: false,
      estado: true,
    });
  };

  const handleDeleteUser = (id) => {
    dispatch(deleteUser(id)).then(({ success, message }) => {
      if (success) {
        dispatch(listUsers());
      } else {
        window.alert(message || 'Error al eliminar el usuario.');
      }
    });
  };

  const sortedUsers = users
    ? [...users].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''))
    : [];
  return (
    <div className={list.container}>
      <section className={list.innerContainer} aria-labelledby="users-title">
        <header className={list.header}>
          <div>
            <h2 id="users-title" className={list.title}>Gestionar usuarios</h2>
            <p className={list.count}>
              {sortedUsers.length} {sortedUsers.length === 1 ? 'usuario' : 'usuarios'}
            </p>
          </div>
          <button type="button" className={list.createButton} onClick={openCreateModal}>
            <MdPersonAdd aria-hidden="true" />
            Crear usuario
          </button>
        </header>

        {loading && <p className={list.loading}>Cargando...</p>}
        {error && <p className={list.error}>Error: {error}</p>}

        <UserModal
          isOpen={isModalOpen}
          onClose={closeModal}
          onSubmit={isEditMode ? handleUpdateUser : handleCreateUser}
          formValues={formValues}
          handleInputChange={handleInputChange}
          isEditMode={isEditMode}
          error={formError}
        />

        <Portal>
          <ConfirmationModal
            isOpen={Boolean(userToDelete)}
            onClose={() => setUserToDelete(null)}
            onConfirm={() => {
              handleDeleteUser(userToDelete.id);
              setUserToDelete(null);
            }}
            message={userToDelete ? `¿Desea eliminar a ${userToDelete.nombre} (${userToDelete.email})?` : ''}
          />
        </Portal>

        {!loading && sortedUsers.length === 0 ? (
          <p className={list.empty}>No hay usuarios.</p>
        ) : (
          <ul className={list.list} aria-label="Lista de usuarios">
            {sortedUsers.map(user => (
              <li key={user.id} className={list.item}>
                <span className={list.avatar} aria-hidden="true">{getInitials(user)}</span>
                <div className={list.info}>
                  <p className={list.name}>{user.nombre}</p>
                  <p className={list.detail}>{user.email}</p>
                </div>
                <div className={list.tags}>
                  {user.is_staff && <span className={styles.tagAdmin}>Administrador</span>}
                  <span className={`${styles.status} ${user.estado ? styles.statusActive : styles.statusInactive}`}>
                    {user.estado ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
                <div className={`${actions.group} ${list.actions}`}>
                  <button
                    type="button"
                    className={actions.iconButton}
                    onClick={() => openEditModal(user)}
                    aria-label={`Editar a ${user.nombre}`}
                    title="Editar"
                  >
                    <MdEdit aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className={`${actions.iconButton} ${actions.danger}`}
                    onClick={() => setUserToDelete(user)}
                    aria-label={`Eliminar a ${user.nombre}`}
                    title="Eliminar"
                  >
                    <MdDelete aria-hidden="true" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default UserManagement;